"use client";

import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type React from "react";
import { Box, type BoxProps } from "./box";
import {
  RING_HIGHLIGHT,
  RING_RADIUS,
  type RoundedToken,
} from "./box-classes";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

export type CardRadiusToken = RoundedToken;

/**
 * Inset for the card's own parts. `spacious` (24px) is the default and is what a
 * page panel wants. A dense card — a kanban card, a compact tile — needs 12px,
 * and with no way to ask for it those call sites bypass the parts entirely and
 * hand-roll Box padding, which is how a header edge and a footer edge inside one
 * card end up at different offsets. It travels as `data-density` so the parts
 * read it off the Card instead of each taking a prop, exactly as `data-table`
 * carries its own density.
 */
export type CardDensity = "compact" | "regular" | "spacious";

// `spacious` is the bare `p-6` on each part, which also covers a part rendered
// outside a Card. These two override it; `in-*` compiles to a zero-specificity
// `:where()`, so it is source order that lets them win.
const CARD_PART_PADDING =
  "in-data-[density=compact]:p-3 in-data-[density=regular]:p-4";

/**
 * Card is a Box wearing the card chrome. Everything structural — `surface`,
 * layout, spacing, `rounded`, `border` — is Box's, so a card that must name a
 * surface passes `surface` and gets that surface's fill, hairline color and shadow
 * from the one implementation. The only thing Card adds is the inner-highlight
 * ring and the card foreground, which are chrome, not depth.
 */
export type CardProps = BoxProps<"div"> & Omittable<{
  /** Corner rounding. Drives the outer corner and the inner highlight ring together. */
  radius?: CardRadiusToken;
  /** Inset applied to `CardHeader`/`CardPanel`/`CardFooter`. */
  density?: CardDensity;
  /**
   * Selection state — washes the card with the brand color (`--primary` at low
   * alpha, so it composites over any substrate and follows the accent preset;
   * under the neutral default theme primary is near-black/white and the wash
   * reads neutral) and emits `data-selected` for callers and tests. While
   * selected, hover deepens the same wash, superseding `hoverBackground`'s
   * plain hover wash.
   */
  selected?: boolean;
}>;

export function Card({
  className,
  radius = "lg",
  surface,
  density = "spacious",
  selected,
  ...props
}: CardProps): React.ReactElement {
  return (
    <Box
      data-density={density}
      data-selected={selected ? "" : undefined}
      className={cn(
        // A Card that NAMES a surface takes that surface's fill, hairline color and
        // shadow from Box and adds only the border WIDTH here. Emitting the
        // default fill alongside would defeat the named surface, because Box merges
        // `className` last — `bg-card` would win over `bg-elevated`.
        surface ? "border" : "border bg-card shadow-xs/5",
        "not-dark:bg-clip-padding text-card-foreground",
        RING_HIGHLIGHT,
        RING_RADIUS[radius],
        // Last in the merge so the wash beats both the default `bg-card` above
        // and a named surface's fill (Box merges className after its own maps).
        // Alphas mirror the neutral wash ramp (selected / selected-hover).
        selected && "bg-primary/15 hover:bg-primary/20",
        className,
      )}
      data-slot="card"
      direction="column"
      surface={surface}
      {...props}
    />
  );
}

export function CardHeader({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 p-6",
      CARD_PART_PADDING,
      "in-[[data-slot=card]:has(>[data-slot=card-panel])]:pb-4 has-data-[slot=card-action]:grid-cols-[1fr_auto]",
      className,
    ),
    "data-slot": "card-header",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardTitle({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn("font-heading font-semibold text-lg leading-none", className),
    "data-slot": "card-title",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardDescription({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn("text-muted-foreground text-sm", className),
    "data-slot": "card-description",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardAction({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "col-start-2 row-span-2 row-start-1 inline-flex self-start justify-self-end",
      className,
    ),
    "data-slot": "card-action",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardPanel({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex-1 p-6",
      CARD_PART_PADDING,
      "in-[[data-slot=card]:has(>[data-slot=card-header]:not(.border-b))]:pt-0 in-[[data-slot=card]:has(>[data-slot=card-footer]:not(.border-t))]:pb-0",
      className,
    ),
    "data-slot": "card-panel",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export function CardFooter({
  className,
  render,
  ...props
}: useRender.ComponentProps<"div">): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex items-center p-6",
      CARD_PART_PADDING,
      "in-[[data-slot=card]:has(>[data-slot=card-panel])]:pt-4",
      className,
    ),
    "data-slot": "card-footer",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}
